import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtMissedVisitForm,
  updateOtMissedVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CalendarIcon,
  CommentsIcon,
  HistoryIcon,
  PhoneIcon,
  TargetIcon,
  UserIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTEMPT_COUNT_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  MISSED_REASON_OPTIONS,
  NOTIFICATION_METHOD_OPTIONS,
  OT_MISSED_ARRAY_KEYS,
  YES_NO_OPTIONS,
  emptyOtMissedVisitPayload,
  toggleInList,
  today,
} from "./otMissedVisitContent";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";

const MISSED_VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const normalizeExclusiveYesNo = (value) => {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    return "";
  }
  const s = String(value || "").trim();
  if (s === "Yes" || s === "No") return s;
  return "";
};

const normalizeAttemptCount = (value) => {
  const s = String(value || "").trim();
  if (ATTEMPT_COUNT_OPTIONS.includes(s)) return s;
  if (s === "3" || s === "4+") return "3+";
  return "";
};

const YesNoToggle = ({ name, value, canEdit, onChange }) => (
  <div className="grid grid-cols-2 gap-1.5">
    {YES_NO_OPTIONS.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          disabled={!canEdit}
          onClick={() => onChange(selected ? "" : opt)}
          className={`h-9 rounded-md border text-[12px] font-semibold transition-colors ${
            selected
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
          aria-pressed={selected}
          name={name}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

YesNoToggle.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const DateTimePair = ({
  dateName,
  timeName,
  dateValue,
  timeValue,
  dateLabel,
  timeLabel,
  canEdit,
  printMode,
  onDateChange,
  onTimeChange,
  dateMax,
}) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label={dateLabel}>
      {printMode ? (
        <div className={`${fieldClass} flex items-center`}>
          {displayDateUS(dateValue) || "—"}
        </div>
      ) : (
        <InputDatePicker
          name={dateName}
          formatSeparator="/"
          allowManualEntry
          disabled={!canEdit}
          value={formatDateForInput(dateValue) || ""}
          onChange={(event) => onDateChange(event.target.value)}
          max={dateMax}
          className="w-full"
          inputClassName={`${fieldClass} bg-white`}
        />
      )}
    </Field>
    <Field label={timeLabel}>
      {printMode ? (
        <div className={`${fieldClass} flex items-center`}>
          {timeValue || "—"}
        </div>
      ) : (
        <input
          type="time"
          name={timeName}
          className={fieldClass}
          value={timeValue || ""}
          disabled={!canEdit}
          onChange={(e) => onTimeChange(e.target.value)}
        />
      )}
    </Field>
  </div>
);

DateTimePair.propTypes = {
  dateName: PropTypes.string.isRequired,
  timeName: PropTypes.string.isRequired,
  dateValue: PropTypes.string,
  timeValue: PropTypes.string,
  dateLabel: PropTypes.string.isRequired,
  timeLabel: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  dateMax: PropTypes.string,
};

const emptyValues = () => ({
  ...emptyOtMissedVisitPayload(),
  missedVisitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const OtMissedVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const clinicianDisplayName = useMemo(
    () =>
      [currentUser?.firstName, currentUser?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim(),
    [currentUser?.firstName, currentUser?.lastName],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtMissedVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        missedVisitDate:
          data.missedVisitDate || loadedPayload.missedVisitDate || today(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        attemptsCount: normalizeAttemptCount(loadedPayload.attemptsCount),
        awareOfNextVisit: normalizeExclusiveYesNo(
          loadedPayload.awareOfNextVisit,
        ),
        planToMakeUpVisit: normalizeExclusiveYesNo(
          loadedPayload.planToMakeUpVisit,
        ),
        reasonOtherSpecify: String(
          loadedPayload.reasonOtherSpecify ||
            loadedPayload.reasonExplanation ||
            "",
        ).trim(),
        interventionsAffected: String(
          loadedPayload.interventionsAffected || "",
        ).trim(),
        functionalImpact: String(
          loadedPayload.functionalImpact || loadedPayload.planToAddress || "",
        ).trim(),
        notificationMethod: String(loadedPayload.notificationMethod || "").trim(),
        personNotified: String(loadedPayload.personNotified || "").trim(),
        notificationDate: String(loadedPayload.notificationDate || "").trim(),
        notificationTime: String(loadedPayload.notificationTime || "").trim(),
        notificationDetails: String(
          loadedPayload.notificationDetails || "",
        ).trim(),
        notifiedBy: String(loadedPayload.notifiedBy || "").trim(),
        contactPerson: String(loadedPayload.contactPerson || "").trim(),
        logDate: String(loadedPayload.logDate || "").trim(),
        logTime: String(loadedPayload.logTime || "").trim(),
        nextVisitDate: String(loadedPayload.nextVisitDate || "").trim(),
        nextVisitTime: String(loadedPayload.nextVisitTime || "").trim(),
        therapistAssigned: String(loadedPayload.therapistAssigned || "").trim(),
        additionalNotes: String(loadedPayload.additionalNotes || "").trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_MISSED_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Missed Visit",
            formType:
              clinicalForm.formType || draft?.formType || "ot_missed_visit",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            missedVisitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyOtMissedVisitPayload(),
              commonHeader,
              notifiedBy: clinicianDisplayName,
            },
          });
          return;
        }

        const res = await getOtMissedVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType:
      form?.formType ||
      draft?.formType ||
      clinicalForm?.formType ||
      "ot_missed_visit",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      missedVisitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...otFields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...otFields,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : "",
      attemptsCount: normalizeAttemptCount(values.attemptsCount),
      awareOfNextVisit: normalizeExclusiveYesNo(values.awareOfNextVisit),
      planToMakeUpVisit: normalizeExclusiveYesNo(values.planToMakeUpVisit),
      reasonOtherSpecify: String(values.reasonOtherSpecify || "").trim(),
      interventionsAffected: String(values.interventionsAffected || "").trim(),
      functionalImpact: String(values.functionalImpact || "").trim(),
      notificationDate: values.notificationDate
        ? formatDateForInput(values.notificationDate)
        : "",
      logDate: values.logDate ? formatDateForInput(values.logDate) : "",
      nextVisitDate: values.nextVisitDate
        ? formatDateForInput(values.nextVisitDate)
        : "",
    });

    OT_MISSED_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtMissedVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.missedVisitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Missed Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Occupational Therapy | Clinical Documentation System
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Missed Visit Date"
          dateValue={displayDateUS(values.missedVisitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Missed Visit Date
                </div>
                <InputDatePicker
                  name="missedVisitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.missedVisitDate) || ""}
                  onChange={(event) =>
                    setValue("missedVisitDate", event.target.value)
                  }
                  max={MISSED_VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
          <div className="lg:col-span-2 min-w-0">
            <Section icon={AlertCircleIcon} title="7. Reason for Missed Visit">
              <CheckboxGrid
                options={MISSED_REASON_OPTIONS}
                values={values.missedReasons}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("missedReasons", opt)}
                columns={2}
              />
              <div className="mt-3">
                <Field label="Other Reason">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.reasonOtherSpecify || ""}
                    disabled={!canEdit}
                    placeholder="Please specify..."
                    onChange={(e) =>
                      setValue("reasonOtherSpecify", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>
          </div>

          <div className="min-w-0">
            <Section icon={PhoneIcon} title="8. Attempts Made">
              <div>
                <SubHeading>Number of Attempts</SubHeading>
                <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                  {ATTEMPT_COUNT_OPTIONS.map((opt) => {
                    const selected = values.attemptsCount === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        disabled={!canEdit}
                        onClick={() =>
                          setValue("attemptsCount", selected ? "" : opt)
                        }
                        className={`h-9 rounded-md border text-sm font-semibold transition-colors ${
                          selected
                            ? "border-indigo-600 bg-indigo-600 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                        } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-3">
                <SubHeading>Methods Used</SubHeading>
                <div className="mt-1.5">
                  <CheckboxGrid
                    options={CONTACT_METHOD_OPTIONS}
                    values={values.contactMethods}
                    canEdit={canEdit}
                    onToggle={(opt) => toggleListValue("contactMethods", opt)}
                    columns={1}
                  />
                </div>
              </div>

              <div className="mt-3 rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-[11px] italic text-sky-800">
                Documentation of at least two attempts is recommended for
                compliance.
              </div>
            </Section>
          </div>
        </div>

        <Section icon={TargetIcon} title="9. Impact on Plan of Care">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
            <div className="space-y-3">
              <div>
                <SubHeading>Patient aware of next visit?</SubHeading>
                <div className="mt-1.5">
                  <YesNoToggle
                    name="awareOfNextVisit"
                    value={values.awareOfNextVisit || ""}
                    canEdit={canEdit}
                    onChange={(opt) => setValue("awareOfNextVisit", opt)}
                  />
                </div>
              </div>
              <div>
                <SubHeading>Plan to make up visit?</SubHeading>
                <div className="mt-1.5">
                  <YesNoToggle
                    name="planToMakeUpVisit"
                    value={values.planToMakeUpVisit || ""}
                    canEdit={canEdit}
                    onChange={(opt) => setValue("planToMakeUpVisit", opt)}
                  />
                </div>
              </div>
            </div>
            <Field label="OT Interventions / Goals Affected">
              <textarea
                rows={6}
                className={`${textareaClass} h-full min-h-[8.5rem]`}
                value={values.interventionsAffected || ""}
                disabled={!canEdit}
                placeholder="Describe affected goals..."
                onChange={(e) =>
                  setValue("interventionsAffected", e.target.value)
                }
              />
            </Field>
            <Field label="Functional Impact on OT Plan of Care">
              <textarea
                rows={6}
                className={`${textareaClass} h-full min-h-[8.5rem]`}
                value={values.functionalImpact || ""}
                disabled={!canEdit}
                placeholder="Provide detailed assessment of functional progress delay or risks..."
                onChange={(e) => setValue("functionalImpact", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={UserIcon}
          title="10. Physician / Case Manager Notification"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field label="Notification Method">
              {printMode || !canEdit ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.notificationMethod || "—"}
                </div>
              ) : (
                <select
                  className={fieldClass}
                  value={values.notificationMethod || ""}
                  onChange={(e) =>
                    setValue("notificationMethod", e.target.value)
                  }
                >
                  <option value="">Select Method...</option>
                  {NOTIFICATION_METHOD_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Person Notified">
              <input
                type="text"
                className={fieldClass}
                value={values.personNotified || ""}
                disabled={!canEdit}
                placeholder="Name and Title/Role"
                onChange={(e) => setValue("personNotified", e.target.value)}
              />
            </Field>
            <Field label="Date">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.notificationDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="notificationDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.notificationDate) || ""}
                  onChange={(event) =>
                    setValue("notificationDate", event.target.value)
                  }
                  className="w-full"
                  inputClassName={`${fieldClass} bg-white`}
                />
              )}
            </Field>
            <Field label="Time">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.notificationTime || "—"}
                </div>
              ) : (
                <input
                  type="time"
                  name="notificationTime"
                  className={fieldClass}
                  value={values.notificationTime || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("notificationTime", e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Details / Response">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.notificationDetails || ""}
                disabled={!canEdit}
                placeholder="Enter details of conversation or response received..."
                onChange={(e) =>
                  setValue("notificationDetails", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
          <Section icon={HistoryIcon} title="11. Notification Log">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Notified By">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.notifiedBy || ""}
                  disabled={!canEdit}
                  placeholder="Clinician name"
                  onChange={(e) => setValue("notifiedBy", e.target.value)}
                />
              </Field>
              <Field label="Contact Person">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.contactPerson || ""}
                  disabled={!canEdit}
                  placeholder="Patient/Caregiver name"
                  onChange={(e) => setValue("contactPerson", e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-3">
              <DateTimePair
                dateName="logDate"
                timeName="logTime"
                dateValue={values.logDate}
                timeValue={values.logTime}
                dateLabel="Date"
                timeLabel="Time"
                canEdit={canEdit}
                printMode={printMode}
                onDateChange={(v) => setValue("logDate", v)}
                onTimeChange={(v) => setValue("logTime", v)}
              />
            </div>
          </Section>

          <Section icon={CalendarIcon} title="12. Next Visit Information">
            <DateTimePair
              dateName="nextVisitDate"
              timeName="nextVisitTime"
              dateValue={values.nextVisitDate}
              timeValue={values.nextVisitTime}
              dateLabel="Next Visit Date"
              timeLabel="Next Visit Time"
              canEdit={canEdit}
              printMode={printMode}
              onDateChange={(v) => setValue("nextVisitDate", v)}
              onTimeChange={(v) => setValue("nextVisitTime", v)}
            />
            <div className="mt-3">
              <Field label="Therapist Assigned">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.therapistAssigned || ""}
                  disabled={!canEdit}
                  placeholder="Search therapist database..."
                  onChange={(e) => setValue("therapistAssigned", e.target.value)}
                />
              </Field>
            </div>
          </Section>
        </div>

        <Section icon={CommentsIcon} title="13. Additional Notes / Comments">
          <textarea
            rows={4}
            className={textareaClass}
            value={values.additionalNotes || ""}
            disabled={!canEdit}
            placeholder="Enter clinical observations, barriers to care, or specific instructions discussed..."
            onChange={(e) => setValue("additionalNotes", e.target.value)}
          />
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="OT Authentication"
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtMissedVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtMissedVisitForm;
